import { assertNever } from '../requirements/index.js';
import { offerGroups } from './offers.js';
/**
 * The Hiring card's five stages (frame 06b `288:215`, #265) — "everyone not yet Active, in the
 * order HR works them".
 *
 * DERIVED, NEVER STORED, and deliberately NOT a second set of buckets. The first two stages are a
 * projection of `offerGroups`, the same five groups the Offers list (#259) is built from, so the
 * dashboard and that list cannot disagree about the same candidate. The last three are a
 * projection of one onboarding person's own checklist, made where the checklist is read.
 *
 * The frame's badge on each stage is the person's LIFECYCLE — "Candidate" on the first two,
 * "Onboarding" on the last three — so the split between them is not a judgement call: a candidate
 * can only ever land in the first two, and someone in onboarding only ever in the last three.
 */
export const hiringStages = ['prepare-offer', 'offer-out', 'onboarding-portal', 'hr-processing', 'orientation'];
/**
 * Which stage one candidate's offer group puts them at.
 *
 * TOTAL over `offerGroups`, through `assertNever` — a sixth group is a compile error naming it
 * here, rather than a candidate who silently stops being counted on the dashboard while still
 * appearing on the Offers list.
 *
 * The groupings, and why:
 *
 * - `to-prepare`, `drafts`, `ready-to-send` -> **Prepare the offer**. All three are the offer still
 *   being HR's to finish: nothing written, a draft, or one reviewed but not sent. The frame's
 *   caption ("Interviewed, no offer yet") describes the commonest of the three, and reading it as
 *   the definition would leave every drafted offer in no stage at all — a person who is on the
 *   Offers list and on nobody's dashboard. `offerGroups` keeps the three DISJOINT, which is what
 *   lets this fold them without double-counting anyone.
 *
 * - `expired` (#301) -> **Prepare the offer**, and it belongs with the three above rather than with
 *   `out-for-signature` for the same reason the group exists at all: the Accept-by date has gone by,
 *   the portal will refuse a signature, so "Waiting on their signature" would be false. What happens
 *   next is new terms, which is HR's move.
 *
 * - `out-for-signature` -> **Offer out to sign**. The frame's own definition.
 *
 * - `signed` -> **Offer out to sign**, and this one is a judgement worth stating. A signed offer
 *   belongs to a person who is still in the `candidate` lifecycle until HR moves them to
 *   Onboarding, so they cannot be shown under an "Onboarding" badge. Of the two stages open to
 *   them, "Prepare the offer" would tell HR to go and write an offer that is already signed, which
 *   is worse than a stage whose caption is a beat behind. It is also the shortest-lived group
 *   there is: moving them to Onboarding is the next thing HR does.
 */
export function candidateHiringStage(group) {
    switch (group) {
        case 'to-prepare':
        case 'drafts':
        case 'ready-to-send':
        case 'expired':
            return 'prepare-offer';
        case 'out-for-signature':
        case 'signed':
            return 'offer-out';
        default:
            return assertNever(group, 'candidateHiringStage');
    }
}
/** Every offer group that folds into one stage. The inverse of `candidateHiringStage`. */
export function offerGroupsInStage(stage) {
    return offerGroups.filter((group) => candidateHiringStage(group) === stage);
}
/**
 * The ONE stage a candidate is at, folded from EVERY offer row they have.
 *
 * WHY THIS EXISTS AT ALL. The Offers list is one row per OFFER (#297), so a candidate holding a
 * signed offer and a fresh draft contributes two rows in two groups. The Hiring card and the
 * dashboard's "offers to prepare" line both count PEOPLE, so the rows have to be folded before they
 * are counted. Counting them raw double-counts that person, which is the mirror image of the failure
 * `Hiring.unplaced` exists to prevent: figures that no longer describe a roster.
 *
 * IT FOLDS ROWS, WHICH IS ALSO ITS LIMIT. A candidate this fold cannot see — because they have no row
 * at all, not because their rows disagree — is in none of the figures it feeds. That is not something
 * to paper over here by inventing a stage for somebody whose state is unknown; it belongs in
 * `Hiring.unplaced`, whose whole job is "held but not placed". So the figures add up to *the candidates
 * the Offers list could speak for*, and anyone it could not has to be counted there. #317 introduces
 * exactly such a case (a stored document that will not parse) and that is where it goes.
 *
 * FURTHEST ALONG WINS, the same precedence the Offers list orders one person's rows by. So a
 * candidate with a signature and a new draft reads as "Offer out to sign", not as somebody HR
 * should go and write an offer for.
 *
 * No rows at all answers `prepare-offer`, which is also what a `to-prepare` row answers, so a
 * caller cannot get a different result by filtering the empty case out first.
 */
export function candidateStageOf(groups) {
    return groups.some((group) => candidateHiringStage(group) === 'offer-out') ? 'offer-out' : 'prepare-offer';
}
/**
 * Each candidate's single stage, keyed by person, from the Offers list's rows.
 *
 * The one place the fold above is applied to a row list, so the Hiring card's stage counts and the
 * dashboard's "offers to prepare" figure cannot drift apart.
 */
export function candidateStagesByPerson(rows) {
    const groups = new Map();
    for (const row of rows) {
        const existing = groups.get(row.personId);
        if (existing === undefined)
            groups.set(row.personId, [row.group]);
        else
            existing.push(row.group);
    }
    return new Map([...groups].map(([personId, list]) => [personId, candidateStageOf(list)]));
}
//# sourceMappingURL=hiring.js.map